import React, { useState } from "react"
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Briefcase,
  User,
  AlertCircle,
  Plus,
  X,
} from "lucide-react"

// ── Field label ───────────────────────────────────────────────────────────────
const FieldLabel = ({ children, required }) => (
  <label
    style={{
      display: "block",
      fontSize: 13,
      fontWeight: 600,
      color: "#374151",
      marginBottom: 6,
      letterSpacing: "0.01em",
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </label>
)

// ── Field error ───────────────────────────────────────────────────────────────
const FieldError = ({ error }) =>
  error ? (
    <p
      style={{ marginTop: 5, fontSize: 13, color: "#ef4444", fontWeight: 500 }}
    >
      {error}
    </p>
  ) : null

// ── Text input ────────────────────────────────────────────────────────────────
const TextInput = ({ error, style: extraStyle = {}, ...props }) => (
  <input
    {...props}
    style={{
      width: "100%",
      height: 38,
      padding: "0 12px",
      background: "#fff",
      border: `1px solid ${error ? "#f87171" : "#e5e7eb"}`,
      borderRadius: 6,
      fontSize: 14,
      fontWeight: 500,
      color: "#111827",
      fontFamily: "'DM Sans', sans-serif",
      outline: "none",
      transition: "border-color 0.15s",
      ...extraStyle,
    }}
    onFocus={(e) => {
      e.target.style.borderColor = "#6b7280"
      props.onFocus?.(e)
    }}
    onBlur={(e) => {
      e.target.style.borderColor = error ? "#f87171" : "#e5e7eb"
      props.onBlur?.(e)
    }}
  />
)

// ── Select dropdown ───────────────────────────────────────────────────────────
const SelectDropdown = ({
  label,
  required,
  value,
  placeholder,
  options,
  onSelect,
  error,
  dataDropdown,
  isOpen,
  onToggle,
}) => {
  const btnRef = React.useRef(null)
  const [dropdownRect, setDropdownRect] = React.useState(null)

  const handleToggle = () => {
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setDropdownRect(rect)
    }
    onToggle()
  }

  return (
    <div style={{ position: "relative" }} data-dropdown={dataDropdown}>
      <FieldLabel required={required}>{label}</FieldLabel>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        style={{
          width: "100%",
          height: 38,
          padding: "0 12px",
          background: "#fff",
          border: `1px solid ${error ? "#f87171" : "#e5e7eb"}`,
          borderRadius: 6,
          fontSize: 14,
          fontWeight: value ? 500 : 400,
          color: value ? "#111827" : "#9ca3af",
          fontFamily: "'DM Sans', sans-serif",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          outline: "none",
          transition: "border-color 0.15s",
        }}
      >
        <span>{value || placeholder}</span>
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#9ca3af"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.15s",
            flexShrink: 0,
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {isOpen && dropdownRect && (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 100 }}
            onClick={handleToggle}
          />
          <div
            style={{
              position: "fixed",
              top: dropdownRect.bottom + 4,
              left: dropdownRect.left,
              width: dropdownRect.width,
              background: "#fff",
              border: "1px solid #e5e7eb",
              borderRadius: 8,
              boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
              zIndex: 101,
              overflow: "hidden",
              maxHeight: 240,
              overflowY: "auto",
            }}
          >
            {options.map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onSelect(opt)}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "9px 12px",
                  fontSize: 14,
                  fontWeight: value === opt ? 600 : 400,
                  color: "#374151",
                  background:
                    value === opt ? "rgb(250,249,245)" : "transparent",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  transition: "background 0.1s",
                }}
                onMouseEnter={(e) => {
                  if (value !== opt) e.target.style.background = "#f3f4f6"
                }}
                onMouseLeave={(e) => {
                  e.target.style.background =
                    value === opt ? "rgb(250,249,245)" : "transparent"
                }}
              >
                {opt}
              </button>
            ))}
          </div>
        </>
      )}
      <FieldError error={error} />
    </div>
  )
}

// ── Section card ──────────────────────────────────────────────────────────────
const SectionCard = ({
  icon: Icon,
  iconColor = "#6b7280",
  title,
  subtitle,
  headerRight,
  children,
}) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e5e7eb",
      borderRadius: 10,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 20px",
        borderBottom: "1px solid #f3f4f6",
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <Icon
          size={14}
          strokeWidth={2}
          style={{ color: iconColor, marginTop: 1, flexShrink: 0 }}
        />
        <div>
          <p
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: "#111827",
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </p>
          {subtitle && (
            <p style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>
              {subtitle}
            </p>
          )}
        </div>
      </div>
      {headerRight && <div style={{ flexShrink: 0 }}>{headerRight}</div>}
    </div>
    <div
      style={{
        padding: "18px 20px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}
    >
      {children}
    </div>
  </div>
)

// ── Weightage toggle ──────────────────────────────────────────────────────────
const WeightageToggle = ({ value, onChange }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      background: "rgb(250,249,245)",
      border: "1px solid #e5e7eb",
      borderRadius: 7,
      padding: 3,
      gap: 2,
    }}
  >
    {["Equal", "Unequal"].map((opt) => (
      <button
        key={opt}
        type="button"
        onClick={() => onChange(opt)}
        style={{
          padding: "4px 10px",
          fontSize: 12,
          fontWeight: 600,
          borderRadius: 5,
          border: "none",
          cursor: "pointer",
          fontFamily: "'DM Sans', sans-serif",
          transition: "all 0.12s",
          background: value === opt ? "#fff" : "transparent",
          color: value === opt ? "#111827" : "#9ca3af",
          boxShadow: value === opt ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
        }}
      >
        {opt}
      </button>
    ))}
  </div>
)

// ── Ideal candidate input (with weightage support) ────────────────────────────
const IdealCandidateInput = ({
  items,
  onAdd,
  onRemove,
  onWeightChange,
  onEdit,
  weightageMode,
  placeholder,
  proTip,
  suggestions,
  error,
}) => {
  const [inputValue, setInputValue] = useState("")
  const [editingIndex, setEditingIndex] = useState(null)

  const safeItems = Array.isArray(items) ? items : []

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      const trimmed = inputValue.trim()
      if (!trimmed) return
      if (editingIndex !== null) {
        onEdit(editingIndex, trimmed)
        setEditingIndex(null)
      } else {
        const alreadyExists = safeItems.some(
          (item) => item.requirement === trimmed,
        )
        if (!alreadyExists) onAdd(trimmed)
      }
      setInputValue("")
    }
    if (e.key === "Escape") {
      setInputValue("")
      setEditingIndex(null)
    }
  }

  const handleRowClick = (item, index) => {
    setInputValue(item.requirement)
    setEditingIndex(index)
  }

  const availableSuggestions = (suggestions || []).filter(
    (s) => !safeItems.some((item) => item.requirement === s),
  )

  const isUnequal = weightageMode === "Unequal"
  const total = safeItems.reduce(
    (sum, item) => sum + (Number(item.weightage) || 0),
    0,
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Rows */}
      {safeItems.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {safeItems.map((item, i) => (
            <div
              key={i}
              onClick={() => handleRowClick(item, i)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "8px 12px",
                border: `1px solid ${editingIndex === i ? "#6b7280" : "#e5e7eb"}`,
                borderRadius: 7,
                background: editingIndex === i ? "#f9fafb" : "#fafafa",
                cursor: "text",
                transition: "all 0.12s",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "#6b7280",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                >
                  {item.requirement}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexShrink: 0,
                }}
              >
                {isUnequal ? (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      border: "1px solid #e5e7eb",
                      borderRadius: 5,
                      background: "#fff",
                      overflow: "hidden",
                      height: 26,
                    }}
                  >
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={item.weightage ?? ""}
                      onChange={(e) => onWeightChange(i, e.target.value)}
                      placeholder="0"
                      style={{
                        width: 36,
                        height: "100%",
                        border: "none",
                        background: "transparent",
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#111827",
                        textAlign: "center",
                        fontFamily: "'DM Sans', sans-serif",
                        outline: "none",
                        padding: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 600,
                        color: "#9ca3af",
                        paddingLeft: 4,
                        paddingRight: 6,
                        borderLeft: "1px solid #f3f4f6",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      %
                    </span>
                  </div>
                ) : (
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#9ca3af",
                      background: "rgb(250,249,245)",
                      border: "1px solid #f3f4f6",
                      borderRadius: 5,
                      padding: "2px 7px",
                    }}
                  >
                    Equal
                  </span>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    onRemove(i)
                    if (editingIndex === i) {
                      setInputValue("")
                      setEditingIndex(null)
                    }
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 2,
                    color: "#d1d5db",
                    display: "flex",
                    transition: "color 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#6b7280"
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#d1d5db"
                  }}
                >
                  <X size={13} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ))}
          {/* Total indicator for unequal mode */}
          {isUnequal && safeItems.length > 0 && (
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                paddingRight: 2,
              }}
            >
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color:
                    total === 100
                      ? "#16a34a"
                      : total > 100
                        ? "#ef4444"
                        : "#9ca3af",
                }}
              >
                Total: {total}%{total === 100 ? " ✓" : " / 100%"}
              </span>
            </div>
          )}
        </div>
      )}

      {/* Input */}
      <div style={{ position: "relative" }}>
        {editingIndex === null ? (
          <Plus
            size={13}
            strokeWidth={2.5}
            style={{
              position: "absolute",
              left: 11,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#9ca3af",
              pointerEvents: "none",
            }}
          />
        ) : (
          <svg
            width="13"
            height="13"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6b7280"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{
              position: "absolute",
              left: 11,
              top: "50%",
              transform: "translateY(-50%)",
              pointerEvents: "none",
            }}
          >
            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
            <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
          </svg>
        )}
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            editingIndex !== null
              ? "Edit and press Enter to save…"
              : placeholder
          }
          style={{
            width: "100%",
            height: 38,
            paddingLeft: 32,
            paddingRight: 90,
            background: editingIndex !== null ? "#f9fafb" : "#fff",
            border: `1px solid ${error ? "#f87171" : editingIndex !== null ? "#6b7280" : "#e5e7eb"}`,
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 400,
            color: "#111827",
            fontFamily: "'DM Sans', sans-serif",
            outline: "none",
            transition: "border-color 0.15s",
          }}
          onFocus={(e) => {
            if (editingIndex === null) e.target.style.borderColor = "#6b7280"
          }}
          onBlur={(e) => {
            if (editingIndex === null)
              e.target.style.borderColor = error ? "#f87171" : "#e5e7eb"
          }}
        />
        <span
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 11,
            fontWeight: 600,
            color: "#d1d5db",
            letterSpacing: "0.04em",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {editingIndex !== null ? "Esc cancel" : "Enter ↵"}
        </span>
      </div>

      {error && <FieldError error={error} />}
      {proTip && (
        <p style={{ fontSize: 13, color: "#9ca3af", marginTop: -4 }}>
          {proTip}
        </p>
      )}

      {/* Suggestions */}
      {availableSuggestions.length > 0 && (
        <div>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "#9ca3af",
              marginBottom: 8,
            }}
          >
            Suggestions
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {availableSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onAdd(s)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "5px 12px",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#374151",
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  transition: "all 0.12s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#9ca3af"
                  e.currentTarget.style.background = "#f9fafb"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb"
                  e.currentTarget.style.background = "#fff"
                }}
              >
                <Plus
                  size={11}
                  strokeWidth={2.5}
                  style={{ color: "#9ca3af" }}
                />
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Dealbreaker toggle ────────────────────────────────────────────────────────
const DealbreakerToggle = ({ checked, onChange, hint }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 9,
        cursor: "pointer",
      }}
      onClick={() => onChange(!checked)}
    >
      <div
        style={{
          width: 17,
          height: 17,
          borderRadius: 4,
          flexShrink: 0,
          border: `1.5px solid ${checked ? "#111827" : "#d1d5db"}`,
          background: checked ? "#111827" : "#fff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.12s",
        }}
      >
        {checked && (
          <svg
            width="9"
            height="9"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
      </div>
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: "#374151",
          userSelect: "none",
        }}
      >
        Mark as dealbreaker
      </span>
    </div>
    {hint && (
      <p style={{ fontSize: 13, color: "#9ca3af", paddingLeft: 26 }}>{hint}</p>
    )}
  </div>
)

// ── Tag list input ────────────────────────────────────────────────────────────
const TagListInput = ({
  items,
  onAdd,
  onRemove,
  placeholder,
  proTip,
  suggestions,
  tagStyle = {},
  dotColor = "#9ca3af",
  error,
}) => {
  const [inputValue, setInputValue] = useState("")

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault()
      const trimmed = inputValue.trim()
      if (trimmed && !items.includes(trimmed)) onAdd(trimmed)
      setInputValue("")
    }
  }

  const availableSuggestions = (suggestions || []).filter(
    (s) => !items.includes(s),
  )

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* Tags */}
      {items.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((item, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "8px 12px",
                border: "1px solid #e5e7eb",
                borderRadius: 7,
                background: "#fafafa",
                ...tagStyle,
              }}
              className="cps-tag-row"
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  minWidth: 0,
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: dotColor,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#374151",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRemove(i)}
                style={{
                  flexShrink: 0,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: 2,
                  color: "#d1d5db",
                  transition: "color 0.1s",
                  display: "flex",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = "#6b7280"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = "#d1d5db"
                }}
              >
                <X size={13} strokeWidth={2.5} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ position: "relative" }}>
        <Plus
          size={13}
          strokeWidth={2.5}
          style={{
            position: "absolute",
            left: 11,
            top: "50%",
            transform: "translateY(-50%)",
            color: "#9ca3af",
            pointerEvents: "none",
          }}
        />
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={{
            width: "100%",
            height: 38,
            paddingLeft: 32,
            paddingRight: 90,
            background: "#fff",
            border: `1px solid ${error ? "#f87171" : "#e5e7eb"}`,
            borderRadius: 6,
            fontSize: 13,
            fontWeight: 400,
            color: "#111827",
            fontFamily: "'DM Sans', sans-serif",
            outline: "none",
            transition: "border-color 0.15s",
          }}
          onFocus={(e) => {
            e.target.style.borderColor = "#6b7280"
          }}
          onBlur={(e) => {
            e.target.style.borderColor = error ? "#f87171" : "#e5e7eb"
          }}
        />
        <span
          style={{
            position: "absolute",
            right: 10,
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: 11,
            fontWeight: 600,
            color: "#d1d5db",
            letterSpacing: "0.04em",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          Enter ↵
        </span>
      </div>

      {error && <FieldError error={error} />}
      {proTip && (
        <p style={{ fontSize: 13, color: "#9ca3af", marginTop: -4 }}>
          {proTip}
        </p>
      )}

      {/* Suggestions */}
      {availableSuggestions.length > 0 && (
        <div>
          <p
            style={{
              fontSize: 11,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "#9ca3af",
              marginBottom: 8,
            }}
          >
            Suggestions
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {availableSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  if (!items.includes(s)) onAdd(s)
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "5px 12px",
                  fontSize: 13,
                  fontWeight: 500,
                  color: "#374151",
                  background: "#fff",
                  border: "1px solid #e5e7eb",
                  borderRadius: 6,
                  cursor: "pointer",
                  fontFamily: "'DM Sans', sans-serif",
                  transition: "all 0.12s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = "#9ca3af"
                  e.currentTarget.style.background = "#f9fafb"
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = "#e5e7eb"
                  e.currentTarget.style.background = "#fff"
                }}
              >
                <Plus
                  size={11}
                  strokeWidth={2.5}
                  style={{ color: "#9ca3af" }}
                />
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const CandidateProfileStep = ({
  formData,
  setFormData,
  errors,
  setErrors,
  onNext,
  onBack,
}) => {
  const [showNoticePeriodDropdown, setShowNoticePeriodDropdown] =
    useState(false)

  const candidateProfile = formData.candidateProfile || {
    noticePeriod: "",
    noticePeriodDealbreaker: false,
    experience: "",
    experienceDealbreaker: false,
    idealCandidateProfile: [],
    redFlags: [],
    weightage: "Equal",
  }

  const [weightageMode, setWeightageMode] = useState(
    candidateProfile.weightage || "Equal",
  )

  const noticePeriodOptions = [
    "Immediate Joiner",
    "Up to 15 days",
    "Up to 30 days",
    "Up to 45 days",
    "Up to 60 days",
    "Up to 90 days",
  ]

  const idealCandidateSuggestions = [
    "Graduated from a top-tier engineering institute (IIT/NIT/BITS)",
    "Has prior startup or fast-paced environment experience",
  ]

  const redFlagSuggestions = [
    "Employment Gaps",
    "Frequent Job Hopping: 3+ jobs in less than 2 years",
  ]

  const idealCandidateProfile = Array.isArray(
    candidateProfile.idealCandidateProfile,
  )
    ? candidateProfile.idealCandidateProfile
    : []
  const redFlags = candidateProfile.redFlags || []

  const handleField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      candidateProfile: { ...prev.candidateProfile, [field]: value },
    }))
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: null }))
  }

  const handleWeightageMode = (mode) => {
    setWeightageMode(mode)
    handleField("weightage", mode)
  }

  const handleAddIdeal = (val) => {
    const current = Array.isArray(candidateProfile.idealCandidateProfile)
      ? candidateProfile.idealCandidateProfile
      : []
    const next = [...current, { requirement: val, weightage: null }]
    handleField("idealCandidateProfile", next)
  }

  const handleRemoveIdeal = (i) => {
    handleField(
      "idealCandidateProfile",
      idealCandidateProfile.filter((_, idx) => idx !== i),
    )
  }

  const handleWeightChange = (i, val) => {
    const updated = idealCandidateProfile.map((item, idx) =>
      idx === i
        ? { ...item, weightage: val === "" ? null : Number(val) }
        : item,
    )
    handleField("idealCandidateProfile", updated)
  }

  const handleEditIdeal = (i, newText) => {
    const updated = idealCandidateProfile.map((item, idx) =>
      idx === i ? { ...item, requirement: newText } : item,
    )
    handleField("idealCandidateProfile", updated)
  }

  const handleNext = () => {
    const newErrors = {}
    if (!candidateProfile.noticePeriod)
      newErrors.noticePeriod = "Notice period is required"
    if (
      candidateProfile.experience === "" ||
      candidateProfile.experience === null ||
      candidateProfile.experience === undefined ||
      Number(candidateProfile.experience) < 0
    )
      newErrors.experience = "Years of experience is required"
    if (idealCandidateProfile.length === 0)
      newErrors.idealCandidateProfile =
        "Add at least one quality describing your ideal candidate"
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    onNext()
  }

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", paddingBottom: 80 }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap');
      `}</style>

      {/* Page heading */}
      <div style={{ marginBottom: 24 }}>
        <h3
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: "#111827",
            letterSpacing: "-0.02em",
            marginBottom: 4,
          }}
        >
          Candidate Profile
        </h3>
        <p style={{ fontSize: 13, color: "#6b7280" }}>
          Define the requirements and qualities you're looking for in
          candidates.
        </p>
      </div>

      {/* Two-column layout */}
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        {/* Left 60% — Ideal Candidate + Red Flags */}
        <div
          style={{
            flex: "0 0 60%",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <SectionCard
            icon={User}
            title="Ideal Candidate Profile"
            subtitle="Qualities and traits of the perfect hire"
            headerRight={
              <WeightageToggle
                value={weightageMode}
                onChange={handleWeightageMode}
              />
            }
          >
            <IdealCandidateInput
              items={idealCandidateProfile}
              onAdd={handleAddIdeal}
              onRemove={handleRemoveIdeal}
              onWeightChange={handleWeightChange}
              onEdit={handleEditIdeal}
              weightageMode={weightageMode}
              placeholder="e.g. IIT grad with strong system design skills"
              proTip="Pro tip: Be specific about background, mindset, and skills"
              suggestions={idealCandidateSuggestions}
              error={errors.idealCandidateProfile}
            />
          </SectionCard>

          <SectionCard
            icon={AlertCircle}
            iconColor="#f87171"
            title="Red Flags"
            subtitle="Candidates with these traits will be filtered out"
          >
            <TagListInput
              items={redFlags}
              onAdd={(val) => handleField("redFlags", [...redFlags, val])}
              onRemove={(i) =>
                handleField(
                  "redFlags",
                  redFlags.filter((_, idx) => idx !== i),
                )
              }
              placeholder="e.g. Switched multiple jobs without completing a year"
              proTip="Pro tip: Be specific about what you want to avoid in resume selection"
              suggestions={redFlagSuggestions}
              tagStyle={{ background: "#fff5f5", borderColor: "#fecaca" }}
              dotColor="#f87171"
            />
          </SectionCard>
        </div>

        {/* Right 40% — Notice Period + Experience */}
        <div
          style={{
            flex: "0 0 calc(40% - 20px)",
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* Notice Period */}
          <SectionCard icon={Clock} title="Notice Period">
            <SelectDropdown
              label="Maximum acceptable notice period"
              required
              value={candidateProfile.noticePeriod}
              placeholder="Select notice period"
              options={noticePeriodOptions}
              dataDropdown="noticeperiod"
              isOpen={showNoticePeriodDropdown}
              onToggle={() => setShowNoticePeriodDropdown((v) => !v)}
              error={errors.noticePeriod}
              onSelect={(opt) => {
                handleField("noticePeriod", opt)
                setShowNoticePeriodDropdown(false)
              }}
            />
            <DealbreakerToggle
              checked={!!candidateProfile.noticePeriodDealbreaker}
              onChange={(val) => handleField("noticePeriodDealbreaker", val)}
              hint="Candidates who cannot meet this notice period will be automatically filtered out."
            />
          </SectionCard>

          {/* Experience */}
          <SectionCard icon={Briefcase} title="Experience">
            <div>
              <FieldLabel required>Years of experience required</FieldLabel>
              <TextInput
                type="number"
                min="0"
                max="50"
                step="0.5"
                value={candidateProfile.experience}
                onChange={(e) => handleField("experience", e.target.value)}
                placeholder="e.g. 3.5"
                error={errors.experience}
              />
              <FieldError error={errors.experience} />
              <p style={{ marginTop: 6, fontSize: 13, color: "#9ca3af" }}>
                Minimum years of relevant experience required for this role
              </p>
            </div>
            <DealbreakerToggle
              checked={!!candidateProfile.experienceDealbreaker}
              onChange={(val) => handleField("experienceDealbreaker", val)}
              hint="Candidates below this experience threshold will be automatically filtered out."
            />
          </SectionCard>
        </div>
      </div>

      {/* Bottom nav */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 240,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #f3f4f6",
          padding: "12px 28px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 10,
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "8px 18px",
            fontSize: 13,
            fontWeight: 600,
            color: "#374151",
            background: "#fff",
            border: "1px solid #e5e7eb",
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "#9ca3af"
            e.currentTarget.style.color = "#111827"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "#e5e7eb"
            e.currentTarget.style.color = "#374151"
          }}
        >
          <ArrowLeft size={14} strokeWidth={2.5} />
          Back
        </button>
        <button
          type="button"
          onClick={handleNext}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 7,
            padding: "8px 20px",
            fontSize: 13,
            fontWeight: 600,
            color: "#fff",
            background: "#111827",
            border: "none",
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
            transition: "background 0.15s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#27272a"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "#111827"
          }}
        >
          Continue
          <ArrowRight size={14} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
}

export default CandidateProfileStep
